/**
 * Contract Addresses
 *
 * All addresses are read from environment variables.
 * See .env.example for required values per chain.
 */

import type { Address } from 'viem';

import { isV22Grid, isV23Grid, isV22OrLaterGrid } from '@/lib/constants/addresses';

/**
 * Pick the address the app uses for attach / RPC paths when env supports
 * either a single `*_ADDRESS` or a comma-separated `*_ADDRESSES` list
 * (mirroring indexer `*_YIELD_ADAPTER_ADDRESSES`). Singular wins when set;
 * otherwise the first non-empty list entry is used.
 */
export function canonicalYieldAdapter(
  single: string | undefined,
  listCsv: string | undefined,
): string {
  const one = (single ?? '').trim();
  if (one) return one;
  for (const part of (listCsv ?? '').split(',')) {
    const a = part.trim();
    if (a) return a;
  }
  return '';
}

const V2_UGM_ADDRESS = (process.env.NEXT_PUBLIC_V2_UGM_ADDRESS || '').toLowerCase();
const HV_UGM_ADDRESS = (process.env.NEXT_PUBLIC_HV_UGM_ADDRESS || '').toLowerCase();

/**
 * Returns true if the given manager address is a V2-shaped
 * UnifiedGridManager (uint256 gridId, struct-returning `seats(...)`).
 * Covers every live UGM generation:
 *   - v2.0 main UGM (`NEXT_PUBLIC_V2_UGM_ADDRESS`)
 *   - v2.1 hvTAKEOVER / boardroom UGM (`NEXT_PUBLIC_HV_UGM_ADDRESS`)
 *   - v2.2 UGMs (env-driven allowlist via
 *     `NEXT_PUBLIC_V22_UGM_ADDRESSES`, mirrored from the indexer)
 *   - v2.3 UGMs (env-driven allowlist via
 *     `NEXT_PUBLIC_V23_UGM_ADDRESSES`, mirrored from the indexer).
 *     v2.3 keeps the V2 grid-id shape; the only differences are the
 *     additional `moduleTransferSeat` selector (generic module-driven
 *     transfer surface — games, DAO tooling, prediction markets, …)
 *     and extended `IGridHooksV23` callbacks, plus the two-axis
 *     pause (`paused` + `gridModulesPaused`) and per-module
 *     `moduleDisabled` flags.
 *
 * Used by helpers without a `parseGridId(poolId).managerAddress`
 * fallback (`simulate.ts`, `drawer-utils.ts`) to select `UGM_ABI`
 * with `BigInt(gridId)` args instead of the V1 `TAKEOVER_MANAGER_ABI`
 * with a `bytes32 poolId`.
 */
export function isV2Manager(managerAddress: string): boolean {
  const lower = managerAddress.toLowerCase();
  return (!!V2_UGM_ADDRESS && lower === V2_UGM_ADDRESS)
      || (!!HV_UGM_ADDRESS && lower === HV_UGM_ADDRESS)
      || isV22OrLaterGrid(managerAddress);
}

/**
 * Returns true if the given manager is the v2.1 (HV / hvTAKEOVER) UGM.
 *
 * v2.0, v2.1 and v2.2 all share the V2 grid-id shape (uint256) and the
 * same adapter lifecycle: yield adapters call `registerAsset` and the
 * manager gates them purely on the global `approvedAdapters` map (set
 * by the guardian via `setApprovedAdapter`). None of the live UGMs
 * expose an `installAdapter` selector — emitting one reverts on the
 * unknown selector.
 *
 * Yield-adapter flows (`useAttachAdapter`) read this to route to the
 * v2.1-bound adapter deployments instead of the v2.0 / v2.2 sets — each
 * adapter contract is `setApprovedAdapter`-bound to exactly one UGM at
 * deploy time, so cross-routing reverts inside `registerAsset` with
 * `NotApprovedAdapter`.
 */
export function isV21Manager(managerAddress: string | null | undefined): boolean {
  if (!managerAddress) return false;
  return !!HV_UGM_ADDRESS && managerAddress.toLowerCase() === HV_UGM_ADDRESS;
}

export const getContractAddresses = () => {
  const usdcAddress = process.env.NEXT_PUBLIC_USDC_ADDRESS;
  const buybackAddress = process.env.NEXT_PUBLIC_TAKEOVER_BUYBACK_ADDRESS;
  const legacyFactoryAddress = process.env.NEXT_PUBLIC_TAKEOVER_LEGACY_FACTORY_ADDRESS;
  const ugmAddress = process.env.NEXT_PUBLIC_V2_UGM_ADDRESS;

  if (!usdcAddress) {
    console.warn('[contracts] NEXT_PUBLIC_USDC_ADDRESS is not set');
  }

  return {
    USDC: (usdcAddress || '') as Address,
    TAKEOVER_BUYBACK: (buybackAddress || '') as Address,
    TAKEOVER_LEGACY_FACTORY: (legacyFactoryAddress || '') as Address,
    UGM: (ugmAddress || '') as Address,
    FLAUNCH_YIELD_ADAPTER: canonicalYieldAdapter(
      process.env.NEXT_PUBLIC_V2_FLAUNCH_YIELD_ADAPTER_ADDRESS,
      process.env.NEXT_PUBLIC_V2_FLAUNCH_YIELD_ADAPTER_ADDRESSES,
    ) as Address,
    V3_YIELD_ADAPTER: canonicalYieldAdapter(
      process.env.NEXT_PUBLIC_V2_V3_YIELD_ADAPTER_ADDRESS,
      process.env.NEXT_PUBLIC_V2_V3_YIELD_ADAPTER_ADDRESSES,
    ) as Address,
    V4_YIELD_ADAPTER: canonicalYieldAdapter(
      process.env.NEXT_PUBLIC_V2_V4_YIELD_ADAPTER_ADDRESS,
      process.env.NEXT_PUBLIC_V2_V4_YIELD_ADAPTER_ADDRESSES,
    ) as Address,
  };
};

/**
 * Resolve the yield-adapter trio bound to a specific UGM.
 *
 * Each adapter contract is `setApprovedAdapter`-bound to exactly one UGM
 * at deploy time, so v2.0, v2.1 (HV), v2.2 and v2.3 each carry their
 * own Flaunch / V3 / V4 adapter set. Routing a deposit through the
 * wrong trio reverts inside `registerAsset` with `NotApprovedAdapter`,
 * so call-sites must pick the trio that matches the manager they're
 * queueing against.
 *
 * Branch order matters:
 *   1. V21 (HV / hvTAKEOVER) UGM → V21 trio.
 *   2. V23 → V23 trio (canonical builder surface; mainnet has the
 *      full trio approved, Sepolia adapters not yet redeployed).
 *   3. V22 (`isV22Grid` env-driven allowlist) → V22 trio (legacy
 *      production grids only).
 *   4. Default → V2.0 trio.
 *
 * Adapter envs are intentionally allowed to be empty until each adapter
 * ships per-network. `useAttachAdapter`'s per-flow `assertReady`
 * enforces the gate per attach type so Flaunch attach still works
 * even while V3/V4 envs are unset.
 */
export function getYieldAdaptersForManager(managerAddress: string | null | undefined): {
  FLAUNCH_YIELD_ADAPTER: Address;
  V3_YIELD_ADAPTER: Address;
  V4_YIELD_ADAPTER: Address;
} {
  if (isV21Manager(managerAddress)) {
    return {
      FLAUNCH_YIELD_ADAPTER: canonicalYieldAdapter(
        process.env.NEXT_PUBLIC_V21_FLAUNCH_YIELD_ADAPTER_ADDRESS,
        process.env.NEXT_PUBLIC_V21_FLAUNCH_YIELD_ADAPTER_ADDRESSES,
      ) as Address,
      V3_YIELD_ADAPTER: canonicalYieldAdapter(
        process.env.NEXT_PUBLIC_V21_V3_YIELD_ADAPTER_ADDRESS,
        process.env.NEXT_PUBLIC_V21_V3_YIELD_ADAPTER_ADDRESSES,
      ) as Address,
      V4_YIELD_ADAPTER: canonicalYieldAdapter(
        process.env.NEXT_PUBLIC_V21_V4_YIELD_ADAPTER_ADDRESS,
        process.env.NEXT_PUBLIC_V21_V4_YIELD_ADAPTER_ADDRESSES,
      ) as Address,
    };
  }
  // V2.3 UGMs need their own adapter trio (each adapter is
  // setApprovedAdapter-bound to exactly one UGM at deploy time, so
  // routing a v2.3 grid through V22 adapters reverts in
  // `registerAsset`). V23 adapter envs are allowed to be empty until
  // those adapters ship; until then `useAttachAdapter`'s `assertReady`
  // gates the flow and the lobby surface still renders fine because
  // every read path (yield rollups, TVL) goes through the indexer.
  if (isV23Grid(managerAddress)) {
    return {
      FLAUNCH_YIELD_ADAPTER: (process.env.NEXT_PUBLIC_V23_FLAUNCH_YIELD_ADAPTER_ADDRESS || '') as Address,
      V3_YIELD_ADAPTER: (process.env.NEXT_PUBLIC_V23_V3_YIELD_ADAPTER_ADDRESS || '') as Address,
      V4_YIELD_ADAPTER: (process.env.NEXT_PUBLIC_V23_V4_YIELD_ADAPTER_ADDRESS || '') as Address,
    };
  }
  if (isV22Grid(managerAddress)) {
    return {
      FLAUNCH_YIELD_ADAPTER: canonicalYieldAdapter(
        process.env.NEXT_PUBLIC_V22_FLAUNCH_YIELD_ADAPTER_ADDRESS,
        process.env.NEXT_PUBLIC_V22_FLAUNCH_YIELD_ADAPTER_ADDRESSES,
      ) as Address,
      V3_YIELD_ADAPTER: canonicalYieldAdapter(
        process.env.NEXT_PUBLIC_V22_V3_YIELD_ADAPTER_ADDRESS,
        process.env.NEXT_PUBLIC_V22_V3_YIELD_ADAPTER_ADDRESSES,
      ) as Address,
      V4_YIELD_ADAPTER: canonicalYieldAdapter(
        process.env.NEXT_PUBLIC_V22_V4_YIELD_ADAPTER_ADDRESS,
        process.env.NEXT_PUBLIC_V22_V4_YIELD_ADAPTER_ADDRESSES,
      ) as Address,
    };
  }
  return {
    FLAUNCH_YIELD_ADAPTER: canonicalYieldAdapter(
      process.env.NEXT_PUBLIC_V2_FLAUNCH_YIELD_ADAPTER_ADDRESS,
      process.env.NEXT_PUBLIC_V2_FLAUNCH_YIELD_ADAPTER_ADDRESSES,
    ) as Address,
    V3_YIELD_ADAPTER: canonicalYieldAdapter(
      process.env.NEXT_PUBLIC_V2_V3_YIELD_ADAPTER_ADDRESS,
      process.env.NEXT_PUBLIC_V2_V3_YIELD_ADAPTER_ADDRESSES,
    ) as Address,
    V4_YIELD_ADAPTER: canonicalYieldAdapter(
      process.env.NEXT_PUBLIC_V2_V4_YIELD_ADAPTER_ADDRESS,
      process.env.NEXT_PUBLIC_V2_V4_YIELD_ADAPTER_ADDRESSES,
    ) as Address,
  };
}

/**
 * Resolve the manager contract address for a given pool.
 * The pool's managerAddress (from the indexer) is required — there is no
 * global fallback. This ensures the correct manager is always targeted,
 * whether it's V1, graduation TFSM, or the GraduationManager itself.
 */
export function getManagerForPool(poolManagerAddress: string): Address {
  if (!poolManagerAddress) {
    console.warn('[contracts] getManagerForPool called without address — returning zero address');
    return '0x0000000000000000000000000000000000000000' as Address;
  }
  return poolManagerAddress as Address;
}
